/**
 * @file authFailureMarker.js — react-app #673
 *
 * `baseApi` has two token-failure paths. The PROACTIVE one
 * (`acquireTokenOrReport`) reports the failure and rethrows an error carrying
 * `isAuthFailure: true`, so a caller that classifies its own telemetry can see
 * the failure was already diagnosed and decline to report a duplicate.
 *
 * The REACTIVE one (a 401 comes back, the silent refresh dies) reports the same
 * way but then falls back to RETURNING the original 401 Response — deliberately,
 * because throwing there would change control flow for every caller, session-end
 * redirects among them. The error is therefore built later, by
 * `parseJsonOrThrow`, from a Response that carries no evidence of what baseApi
 * already did.
 *
 * This module is the evidence: baseApi stamps the Response it falls back to,
 * and `responseUtils` forwards the stamp onto the error it throws. Kept in its
 * own module so `responseUtils` never has to import `baseApi` (which pulls in
 * React hooks and Sentry) to read one boolean.
 *
 * A Symbol, not a string key: a Response's own properties sit next to
 * server-influenced data, and a Symbol cannot collide with a field name, cannot
 * be reached by `JSON.stringify`, and cannot be enumerated into a telemetry
 * payload by accident. The marker is a boolean about OUR control flow — it
 * carries no request, response, or client data.
 */

const AUTH_FAILURE_REPORTED = Symbol("gma.authFailureReported");

/**
 * Record that baseApi has already reported the auth failure behind this
 * response, and is falling back to returning it.
 *
 * Never throws: a real `Response` is extensible, but a caller's test double or
 * a frozen object must not be able to break a request over telemetry
 * bookkeeping — the same policy the reporters in `baseApi` follow.
 *
 * @param {Response|object|null} response
 * @returns {Response|object|null} the same response, for call-site brevity
 */
export const markAuthFailureReported = (response) => {
  try {
    if (response && typeof response === "object") {
      Object.defineProperty(response, AUTH_FAILURE_REPORTED, {
        value: true,
        // Non-enumerable and non-writable: nothing downstream should see or
        // change it, and a second mark on a shared response is a no-op.
        enumerable: false,
        configurable: true,
      });
    }
  } catch (_ignored) {
    // Frozen or exotic object — the marker is an optimisation for caller
    // telemetry, never a correctness requirement.
  }
  return response;
};

/**
 * @param {Response|object|null} response
 * @returns {boolean} whether baseApi already reported this response's auth failure
 */
export const wasAuthFailureReported = (response) => {
  try {
    return Boolean(response?.[AUTH_FAILURE_REPORTED]);
  } catch (_ignored) {
    return false;
  }
};
